import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { getYoxlamaStats, YoxlamaStats } from '../../services/adminService';
import { logout } from '../../services/authService';
import ExamSelector from './ExamSelector';
import Icon from '../icons/Icon';
import { getSelectedExam, setSelectedExam, clearSelectedExam, enrichExamBlankScheme } from '../../services/examService';
import { Exam } from '../../types';

interface AdminDashboardProps {
  adminName: string;
  adminId: string;
}

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #1e3a8a;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  ${props => {
    switch (props.variant) {
      case 'danger':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          color: #475569;
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(100, 116, 139, 0.2);
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          }
        `;
    }
  }}

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
    flex: 1;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StatCard = styled.div<{ color?: string }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${props => props.color || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const StatDescription = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ActionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const ActionIcon = styled.div<{ color?: string }>`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${props => props.color || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    font-size: 28px;
  }
`;

const ActionTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ActionDescription = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Modal = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 2000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 24px 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
  display: block;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FormButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  margin-top: 8px;

  ${props => props.variant === 'secondary' ? `
    background: #f1f5f9;
    color: #475569;
    &:hover {
      background: #e2e8f0;
    }
  ` : `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;

  & > button {
    flex: 1;
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 8px;
`;

const SuccessMessage = styled.div`
  background: #dcfce7;
  color: #166534;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 8px;
`;

const AdminDashboard: React.FC<AdminDashboardProps> = ({ adminName, adminId }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<YoxlamaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedExam, setSelectedExamState] = useState<Exam | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [showExamSelector, setShowExamSelector] = useState(false);
  const [showExamModal, setShowExamModal] = useState(false);
  const [examForm, setExamForm] = useState({
    collection: '',
    name: '',
    type: 'Blok' as 'Blok' | 'buraxilis'
  });
  const [isCreating, setIsCreating] = useState(false);
  const [examMessage, setExamMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    // Check if exam is already selected. Enrich blank scheme if missing. Do NOT auto-fetch heavy stats.
    let cancelled = false;
    (async () => {
      const storedExam = getSelectedExam();
      if (storedExam) {
        try {
          const enriched = await enrichExamBlankScheme(storedExam);
          if (!cancelled) {
            setSelectedExam(enriched);
            setSelectedExamState(enriched);
          }
        } catch {
          if (!cancelled) setSelectedExamState(storedExam);
        }
      } else if (!cancelled) {
        setShowExamSelector(true);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExamSelect = async (exam: Exam) => {
    try {
      const enriched = await enrichExamBlankScheme(exam);
      setSelectedExam(enriched);
      setSelectedExamState(enriched);
    } catch {
      setSelectedExam(exam);
      setSelectedExamState(exam);
    }
    setShowExamSelector(false);
    // Do not auto-load stats — wait for explicit Sync action from the admin
  };

  const handleChangeExam = () => {
    setShowExamSelector(true);
  };

  const loadStats = async (exam: Exam) => {
    try {
      setLoadingStats(true);
      const examType = exam.type === 'buraxilis' ? 'buraxilis' : 'blok';
      const data = await getYoxlamaStats(exam.collection, examType);
      setStats(data);
      setIsSynced(true);
      setLastSyncedAt(Date.now());
    } catch (error) {
      console.error('Statistikalar yüklənərkən xəta:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSync = async () => {
    if (!selectedExam) return;
    await loadStats(selectedExam);
  };

  const handleOpenExamModal = () => {
    setShowExamModal(true);
    setExamForm({ collection: '', name: '', type: 'Blok' });
    setExamMessage(null);
  };

  const handleCloseExamModal = () => {
    setShowExamModal(false);
    setExamForm({ collection: '', name: '', type: 'Blok' });
    setExamMessage(null);
  };

  const handleCreateExam = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!examForm.collection || !examForm.name) {
      setExamMessage({ type: 'error', text: 'Bütün xanaları doldurun' });
      return;
    }

    try {
      setIsCreating(true);
      setExamMessage(null);

      const { createExam } = await import('../../services/examService');
      const result = await createExam(examForm.collection, examForm.name, examForm.type);

      if (result.success) {
        setExamMessage({ type: 'success', text: result.message });
        // Auto-close after success
        setTimeout(() => {
          handleCloseExamModal();
          // Optionally refresh the exam list if needed
        }, 1500);
      } else {
        setExamMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      console.error('Error creating exam:', error);
      setExamMessage({ type: 'error', text: 'İmtahan yaradılarkən xəta baş verdi' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleLogout = async () => {
    try {
      clearSelectedExam();
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Çıxış edilərkən xəta:', error);
    }
  };

  if (showExamSelector && !selectedExam) {
    // First-time: must select, no close
    return <ExamSelector onExamSelect={handleExamSelect} />;
  }

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <div>Yüklənir...</div>
        </LoadingContainer>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <HeaderLeft>
          <Title>Admin Panel</Title>
          <Subtitle>Xoş gəlmisiniz, {adminName}</Subtitle>
          {selectedExam && (
            <div style={{ 
              marginTop: '8px', 
              fontSize: '14px', 
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
                <span style={{
                  background: selectedExam.type === 'buraxilis' 
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                {selectedExam.type === 'buraxilis' ? 'Buraxılış' : 'Blok'}
                </span>
                <span>{selectedExam.name}</span>
              </div>
            )}
        </HeaderLeft>
        <HeaderRight>
          <Button variant="primary" onClick={handleOpenExamModal}>
            <Icon name="add" size={16} color="white" style={{ marginRight: 8 }} /> Yeni İmtahan
          </Button>
          <Button variant="secondary" onClick={handleChangeExam}>
            <Icon name="refresh" size={16} color="#475569" style={{ marginRight: 8 }} /> İmtahan Dəyiş
          </Button>
          <Button variant="secondary" onClick={handleSync} disabled={!selectedExam || loadingStats}>
            <Icon name="sync" size={16} color="#475569" style={{ marginRight: 8 }} />
            {loadingStats ? 'Sinxronlaşdırılır...' : 'Sinxronlaşdır'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/admin/dashboard')}>
            <Icon name="stats" size={16} color="#475569" style={{ marginRight: 8 }} /> Panel
          </Button>
          <Button variant="danger" onClick={handleLogout}>
            <Icon name="logout" size={16} color="white" style={{ marginRight: 8 }} /> Çıxış
          </Button>
        </HeaderRight>
      </Header>

      <StatsGrid>
          <StatCard color="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
            <StatLabel>Ümumi Yoxlama Məlumatı</StatLabel>
            <StatValue>{stats?.totalScannedData || 0}</StatValue>
            <StatDescription>Sistemdəki bütün yoxlama məlumatları</StatDescription>
          </StatCard>

        <StatCard color="linear-gradient(135deg, #10b981 0%, #059669 100%)">
          <StatLabel>Təyin Edilmiş Tapşırıqlar</StatLabel>
          <StatValue>{stats?.totalAssignments || 0}</StatValue>
          <StatDescription>
            {stats?.activeAssignments || 0} aktiv • {stats?.completedAssignments || 0} tamamlandı
          </StatDescription>
        </StatCard>

        <StatCard color="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
          <StatLabel>Tamamlanan Yoxlamalar</StatLabel>
          <StatValue>{stats?.totalCompleted || 0}</StatValue>
          <StatDescription>{stats?.totalRemaining || 0} yoxlama qaldı</StatDescription>
        </StatCard>

        <StatCard color="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)">
          <StatLabel>Aktiv Müəllimlər</StatLabel>
          <StatValue>{stats?.activeTeachers || 0}</StatValue>
          <StatDescription>Ümumi {stats?.totalTeachers || 0} müəllim</StatDescription>
        </StatCard>
      </StatsGrid>

      <QuickActionsGrid>
        <ActionCard onClick={() => navigate('/admin/teachers')}>
          <ActionIcon color="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
            <Icon name="teacher" size={26} color="white" />
          </ActionIcon>
          <ActionTitle>Müəllim İdarəetməsi</ActionTitle>
          <ActionDescription>
            Müəllimləri görüntüləyin, təyin edin və idarə edin
          </ActionDescription>
        </ActionCard>

        <ActionCard onClick={() => navigate('/admin/assignments')}>
          <ActionIcon color="linear-gradient(135deg, #10b981 0%, #059669 100%)">
            <Icon name="assignments" size={26} color="white" />
          </ActionIcon>
          <ActionTitle>Tapşırıq İdarəetməsi</ActionTitle>
          <ActionDescription>
            Bütün tapşırıqları görüntüləyin və idarə edin
          </ActionDescription>
        </ActionCard>

        <ActionCard onClick={() => navigate('/admin/statistics')}>
          <ActionIcon color="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
            <Icon name="stats" size={26} color="white" />
          </ActionIcon>
          <ActionTitle>Ətraflı Statistikalar</ActionTitle>
          <ActionDescription>
            Sinif, qrup və fənn əsaslı ətraflı statistikalar
          </ActionDescription>
        </ActionCard>

        <ActionCard onClick={() => navigate('/admin/students')}>
          <ActionIcon color="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)">
            <Icon name="student" size={26} color="white" />
          </ActionIcon>
          <ActionTitle>Şagird Nəticələri</ActionTitle>
          <ActionDescription>
            Bütün şagirdləri görüntüləyin və yoxlama nəticələrini incələyin
          </ActionDescription>
        </ActionCard>

        <ActionCard onClick={() => navigate('/admin/exams')}>
          <ActionIcon color="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
            <Icon name="archive" size={26} color="white" />
          </ActionIcon>
          <ActionTitle>İmtahan Arxivi</ActionTitle>
          <ActionDescription>
            İmtahanları arxivləşdirin və idarə edin
          </ActionDescription>
        </ActionCard>

        <ActionCard onClick={() => navigate('/admin/question-images')}>
          <ActionIcon color="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)">
            <Icon name="photo" size={26} color="white" />
          </ActionIcon>
          <ActionTitle>Sual Şəkilləri</ActionTitle>
          <ActionDescription>
            Buraxılış suallarının şəkillərini yükləyin və idarə edin
          </ActionDescription>
        </ActionCard>
      </QuickActionsGrid>

      {/* Exam Creation Modal */}
      <Modal show={showExamModal} onClick={handleCloseExamModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Yeni İmtahan Yarat</ModalTitle>

          <Form onSubmit={handleCreateExam}>
            <div>
              <FormLabel>Collection Adı *</FormLabel>
              <FormInput
                type="text"
                value={examForm.collection}
                onChange={(e) => setExamForm({ ...examForm, collection: e.target.value })}
                placeholder="məs: blok2"
                disabled={isCreating}
              />
            </div>

            <div>
              <FormLabel>İmtahan Adı *</FormLabel>
              <FormInput
                type="text"
                value={examForm.name}
                onChange={(e) => setExamForm({ ...examForm, name: e.target.value })}
                placeholder="məs: Blok İmtahanı - 2"
                disabled={isCreating}
              />
            </div>

            <div>
              <FormLabel>İmtahan Növü *</FormLabel>
              <FormSelect
                value={examForm.type}
                onChange={(e) => setExamForm({ ...examForm, type: e.target.value as 'Blok' | 'buraxilis' })}
                disabled={isCreating}
              >
                <option value="Blok">Blok İmtahanı</option>
                <option value="buraxilis">Buraxılış İmtahanı</option>
              </FormSelect>
            </div>

            {examMessage && examMessage.type === 'error' && (
              <ErrorMessage>{examMessage.text}</ErrorMessage>
            )}

            {examMessage && examMessage.type === 'success' && (
              <SuccessMessage>{examMessage.text}</SuccessMessage>
            )}

            <ButtonGroup>
              <FormButton
                type="button"
                variant="secondary"
                onClick={handleCloseExamModal}
                disabled={isCreating}
              >
                Ləğv et
              </FormButton>
              <FormButton
                type="submit"
                variant="primary"
                disabled={isCreating}
              >
                {isCreating ? 'Yaradılır...' : 'Yarat'}
              </FormButton>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>

      {/* Exam Change Overlay — shown on top of dashboard when user clicks İmtahan Dəyiş */}
      {showExamSelector && selectedExam && (
        <ExamSelector
          onExamSelect={handleExamSelect}
          currentExam={selectedExam}
          onClose={() => setShowExamSelector(false)}
        />
      )}
    </DashboardContainer>
  );
};

export default AdminDashboard;

